import React from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Flex,
  Spinner,
  Center,
  Divider,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { KPIConfiguration } from 'src/components/molecules/KPIConfiguration';
import { ModelHeader } from 'src/components/molecules/ModelHeader';
import { useModelForm } from '../../hooks/useModelForm';
import { MediaSelectList } from '../molecules/MediaSelectList';
import { CampaignSelectList } from '../molecules/CampaignSelectList';

type CreateModelPageProps = {
  onComplete: () => void;
};

export const CreateModelPage: React.FC<CreateModelPageProps> = ({
  onComplete,
}) => {
  const {
    models,
    kpiSources,
    kpiMetrics,
    kpiCategoryOptions,
    mediaPlatforms,
    campaignsWithGroups,
    isLoading,
    isSubmitting,
    areAllModelsValid,
    toastState,
    hideToast,
    handleModelNameChange,
    handleSourceChange,
    handleSourceReset,
    handleMetricChange,
    handleMetricReset,
    handleCategoryChange,
    handleCategoryReset,
    handleMediaChange,
    handleCampaignSelectionsChange,
    handleAddModel,
    handleRemoveModel,
    handleSubmit,
    handleCancel,
  } = useModelForm({ mode: 'create', onComplete });

  if (isLoading && models[0].modelName === '') {
    return (
      <Center py={20}>
        <Spinner color="icon.green" size="lg" />
      </Center>
    );
  }

  return (
    <>
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />
      <Box
        bg="surface.card"
        borderRadius="16px"
        border="1px solid"
        borderColor="border.layout"
        w="100%"
        p={6}>
        {/* Page Title */}
        <Text fontSize="16px" fontWeight={600} color="text.title" mb={6}>
          {models.length > 1 ? 'CREATE NEW MODELS' : 'CREATE NEW MODEL'}
        </Text>

        {models.map((model, index) => (
          <Box key={model.id} mb={index < models.length - 1 ? 8 : 0}>
            {/* Model Header (for multiple models) */}
            <ModelHeader
              modelIndex={index}
              modelsLength={models.length}
              onRemove={() => handleRemoveModel(model.id)}
              canRemove={models.length > 1}
            />

            <VStack spacing={6} align="stretch">
              {/* Model Name */}
              <Box>
                <FormControl>
                  <FormLabel
                    fontSize="16px"
                    fontWeight={500}
                    color="text.title"
                    mb={3}>
                    Model name
                  </FormLabel>
                  <Input
                    placeholder="Name your model"
                    value={model.modelName}
                    onChange={(e) =>
                      handleModelNameChange(model.id, e.target.value)
                    }
                    bg="surface.card"
                    border="1px solid"
                    borderColor="border.layout"
                    borderRadius="md"
                    py={3}
                    px={4}
                    fontSize="16px"
                    _placeholder={{ color: 'gray.500' }}
                    _focus={{
                      borderColor: 'icon.green',
                      boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                    }}
                  />
                </FormControl>
              </Box>

              <KPIConfiguration
                model={model}
                onSourceChange={handleSourceChange}
                onSourceReset={handleSourceReset}
                onMetricChange={handleMetricChange}
                onMetricReset={handleMetricReset}
                onCategoryChange={handleCategoryChange}
                onCategoryReset={handleCategoryReset}
                kpiSources={kpiSources}
                kpiMetrics={kpiMetrics}
                kpiCategories={kpiCategoryOptions}
                isLoading={isLoading}
              />

              <MediaSelectList
                mediaPlatforms={mediaPlatforms}
                selectedMedia={model.selectedMedia}
                onChange={(selectedMedia) =>
                  handleMediaChange(model.id, selectedMedia)
                }
                isDisabled={isLoading}
              />

              <CampaignSelectList
                campaigns={campaignsWithGroups}
                selectedCampaigns={model.campaignSelections}
                onChange={(selections) =>
                  handleCampaignSelectionsChange(model.id, selections)
                }
                isDisabled={isLoading || model.selectedMedia.length === 0}
                emptyMessage={
                  model.selectedMedia.length === 0
                    ? 'Select media above to load campaigns'
                    : 'No campaigns available'
                }
              />
            </VStack>

            {index < models.length - 1 && (
              <Divider my={8} borderColor="border.layout" />
            )}
          </Box>
        ))}

        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify={{ base: 'stretch', md: 'flex-end' }}
          alignItems="center"
          gap={4}
          mt={8}>
          <Button
            buttonName="cancel-create-model"
            variant="outline"
            borderColor="border.box"
            color="text.title"
            bg="surface.card"
            borderRadius="lg"
            w={{ base: '100%', md: '160px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleCancel}
            _hover={{ bg: 'surface.controlHover' }}>
            Cancel
          </Button>

          <Button
            buttonName="add-new-model"
            leftIcon={<Icon iconName="plus" />}
            variant="outline"
            borderColor="icon.green"
            color="text.title"
            bg="surface.card"
            borderRadius="lg"
            w={{ base: '100%', md: '240px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleAddModel}
            _hover={{ bg: 'icon.green', color: 'white' }}>
            Add new model (290 €/mo)
          </Button>

          <Button
            buttonName="submit-create-model"
            bg="icon.green"
            color="white"
            borderRadius="lg"
            w={{ base: '100%', md: '200px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Creating..."
            isDisabled={!areAllModelsValid || isSubmitting}
            _hover={{ opacity: 0.9 }}
            _disabled={{
              bg: 'icon.green',
              cursor: 'not-allowed',
              opacity: 0.5,
            }}>
            Create Model{models.length > 1 ? 's' : ''}
          </Button>
        </Flex>
      </Box>
    </>
  );
};
